import {
  Elements,
  ExpressCheckoutElement,
  PaymentElement,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";
import { OnApproveData, OnApproveActions } from "@paypal/paypal-js";
import { PayPalButtons } from "@paypal/react-paypal-js";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import LoadingButton from "./LoadingButton";
import ShippingRates from "./ShippingRates";
import config from "../utils/config";
import ErrorAlert from "./ErrorAlert";
import {
  CartAddressUserError,
  CartAddressUserErrorDetail,
  useCartContext,
} from "../CartContext";
import {
  ICheckout,
  ICustomerAddress,
  IShippingAddress,
  IShippingMethod,
} from "../types/types";
import AddressInput, { addressSchema } from "./AddressInput";
import * as yup from "yup";
import formatAddress from "../utils/formatAddress";
import AddressSelector from "./AddressSelector";
import TextInput from "./TextInput";
import { Transition } from "@headlessui/react";
import {
  Stripe,
  StripeElementsOptions,
  StripeExpressCheckoutElementReadyEvent,
  StripeExpressCheckoutElementShippingAddressChangeEvent,
} from "@stripe/stripe-js";
import { postcodeToRegion } from "../utils/postcodeToRegion";
import trackingService from "../analytics/trackingService";
import formatPrice from "../utils/formatPrice";
import { ValidationError } from "yup";
import { useLocation, useNavigate } from "react-router-dom";
import {
  OnShippingAddressChangeActions,
  OnShippingAddressChangeData,
} from "@paypal/paypal-js/types/components/buttons";
import * as Sentry from "@sentry/react";
import classNames from "../utils/classNames";
import CombinedCodeForm from "./CombinedCodeForm";
import getShippingRateDisplayPrice from "../utils/getShippingRateDisplayPrice";
import LoadingSpinner from "./LoadingSpinner";
import { CharityDonation } from "../utils/getCharityDonation";
import { describeLogoutFailure } from "../utils/buyerIdentityUpdate";

const addressDetailsErrorMessage =
  "There is a problem with your address details. Please check your details and try again.";
const discountCodeUsedErrorMessage = "Discount code has already been used";
const addressUserErrorFieldMap: Record<string, string> = {
  address1: "address1",
  address2: "address2",
  city: "city",
  country: "country",
  countryCode: "country",
  firstName: "firstName",
  lastName: "lastName",
  phone: "phone",
  province: "province",
  provinceCode: "province",
  zip: "zip",
};
const addressFieldNames = [
  "firstName",
  "lastName",
  "address1",
  "address2",
  "city",
  "province",
  "country",
  "zip",
  "phone",
];

function mapCartAddressUserErrors(
  userErrors: CartAddressUserErrorDetail[],
): Record<string, string[]> {
  return userErrors.reduce<Record<string, string[]>>((fieldErrors, userError) => {
    const field = userError.field
      ?.slice()
      .reverse()
      .find((fieldPart): fieldPart is string =>
        Boolean(fieldPart && addressUserErrorFieldMap[fieldPart]),
      );
    const errorKey = field ? addressUserErrorFieldMap[field] : "shippingAddress";
    fieldErrors[errorKey] = [...(fieldErrors[errorKey] ?? []), userError.message];
    return fieldErrors;
  }, {});
}

function getLocallyValidAddressFields(address: Partial<IShippingAddress> | null) {
  if (!address) {
    return [];
  }

  return addressFieldNames.filter((fieldName) => {
    try {
      addressSchema.validateSyncAt(fieldName, address);
      return true;
    } catch {
      return false;
    }
  });
}

function getPaymentErrorMessage(paymentMethod: string, error: unknown) {
  const errorMessage = error instanceof Error ? error.message : String(error);
  if (errorMessage.includes("has already been used")) {
    return discountCodeUsedErrorMessage;
  }

  return `Error processing payment with ${paymentMethod}: ${errorMessage} Please try again or contact support if you continue to have issues`;
}

function getDefaultPaymentMethod() {
  if (config.paymentEnabled.stripe) {
    return "stripe";
  }
  if (config.paymentEnabled.farmlands) {
    return "farmlands";
  }
  if (config.paymentEnabled.gem) {
    return "gem";
  }
  if (config.paymentEnabled.poli) {
    return "poli";
  }
  return "";
}

const checkoutFormSchema = yup
  .object({
    email: yup.string().email().required(),
    shippingMethod: yup
      .object({
        handle: yup.string().required("Please select a shipping method"),
        groupId: yup.string(),
        price: yup.string().required("Please select a shipping method"),
        title: yup.string().required("Please select a shipping method"),
      })
      .required("Please select a shipping method"),
  })
  .test("address", "Please enter a shipping address", function (value) {
    const { shippingAddress, customer } = (value as ICheckout) || {};

    if (!shippingAddress && !customer?.preferredAddress) {
      return this.createError({
        path: "shippingAddress",
        message: "Please enter a shipping address",
      });
    }

    return true;
  });

export interface IErrorState {
  error: string;
}
export function isErrorState(state: any): state is IErrorState {
  if (!state) {
    return false;
  }
  return (
    typeof state === "object" &&
    "error" in state &&
    typeof state.error === "string"
  );
}

function CheckoutExpressPaymentMethods({
  checkout,
  expectedShippingCents,
  enabledPaymentMethods,
  setEnabledPaymentMethods,
  expressElementsLoading,
  setExpressElementsLoading,
  handleExpressShippingAddressChange,
}: {
  checkout: ICheckout;
  expectedShippingCents?: number;
  enabledPaymentMethods: string[];
  setEnabledPaymentMethods: (paymentMethods: string[]) => void;
  expressElementsLoading: boolean;
  setExpressElementsLoading: (loading: boolean) => void;
  handleExpressShippingAddressChange: (
    event: StripeExpressCheckoutElementShippingAddressChangeEvent,
  ) => Promise<void>;
}) {
  const elements = useElements();
  const stripe = useStripe();

  async function handleStripeExpressPayment(checkout: ICheckout) {
    if (!stripe || !elements || !checkout) {
      throw new Error("Stripe not initialised");
    }
    const { error: submitError } = await elements.submit();
    if (submitError) {
      throw new Error(
        submitError.message ??
        "Something went wrong with your payment, please try again or contact support",
      );
    }

    const sessionResponse = await fetch(
      `${config.checkoutUrl}/api/createSession`,
      {
        method: "POST",
        body: JSON.stringify({
          cartId: checkout.cartId,
          provider: "stripe",
          expectedShippingCents,
        }),
      },
    );

    if (!sessionResponse.ok) {
      throw new Error(await sessionResponse.text());
    }

    const { sessionData } = await sessionResponse.json();
    const cartId = checkout.cartId.replace("gid://shopify/Cart/", "");

    const { error } = await stripe.confirmPayment({
      elements,
      clientSecret: sessionData.sessionId,
      confirmParams: {
        return_url: `${config.checkoutUrl}/checkout/confirm/${cartId}`,
      }
    });

    if (error) {
      throw new Error(
        error.message ??
        "Something went wrong with your payment, please try again or contact support",
      );
    }
  }

  return (
    <ExpressCheckoutElement
      className={classNames(
        !expressElementsLoading && enabledPaymentMethods.length === 0
          ? "hidden"
          : "",
        enabledPaymentMethods.length === 2 ? "md:w-[65%]" : "",
        enabledPaymentMethods.length === 1 ? "md:w-[50%]" : "",
        "w-full",
      )}
      options={{
        layout: {
          maxColumns: 2,
          maxRows: 2,
        },
        buttonTheme: {
          applePay: "black",
        },
        buttonType: {
          googlePay: "plain",
        },
        buttonHeight: 50,
      }}
      onClick={({ resolve }) => {
        resolve({
          emailRequired: true,
          shippingAddressRequired: true,
          allowedShippingCountries: [config.countryCode],
          shippingRates: [{
            id: 'default',
            amount: 5000,
            displayName: 'Shipping',
          }],
        });
      }}
      onConfirm={() => {
        handleStripeExpressPayment(checkout);
      }}
      onReady={(event: StripeExpressCheckoutElementReadyEvent) => {
        setExpressElementsLoading(false);
        if (event.availablePaymentMethods) {
          const enabledMethods = Object.keys(
            event.availablePaymentMethods,
          ).filter(
            (key) => (event.availablePaymentMethods as any)[key] === true,
          );
          setEnabledPaymentMethods(enabledMethods);
        }
      }}
      onShippingAddressChange={handleExpressShippingAddressChange}
    />
  );
}

const CheckoutForm = ({
  onShowDetails,
  stripePromise,
  selectedStripePaymentMethod,
  setSelectedStripePaymentMethod,
  donationOption,
  onDonationOptionChange,
  otherDonationAmount,
  onOtherDonationAmountChange,
  donationAmount,
  roundUpDonationAmount,
  roundedUpCheckoutTotal,
  checkoutTotalWithDonation,
  charityDonation,
}: {
  onShowDetails: () => void;
  stripePromise: Promise<Stripe | null>;
  selectedStripePaymentMethod: string;
  setSelectedStripePaymentMethod: (paymentMethod: string) => void;
  donationOption: string | null;
  onDonationOptionChange: (option: string | null) => void;
  otherDonationAmount: string;
  onOtherDonationAmountChange: (amount: string) => void;
  donationAmount: number;
  roundUpDonationAmount: number;
  roundedUpCheckoutTotal: number;
  checkoutTotalWithDonation: string;
  charityDonation: CharityDonation | null;
}) => {
  const checkoutFormRef = useRef<null | HTMLFormElement>(null);
  const {
    checkout,
    checkoutTotals,
    cartLoading,
    paymentInProgress,
    setPaymentInProgress,
    totalsLoading,
    updateShippingMethod,
    updateShippingAddress,
    updateCustomerPreferredAddress,
    updateEmail,
    removeUserFromCart,
    toggleEmailMarketingOptIn,
    validateDiscountCodes,
  } = useCartContext();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [logoutError, setLogoutError] = useState<string | null>(null);
  const [email, setEmail] = useState("");
  const [manualShippingAddress, setManualShippingAddress] =
    useState<Partial<ICustomerAddress> | null>(
      checkout?.shippingAddress ?? { country: config.countryCode },
    );
  const [manualBillingAddress, setManualBillingAddress] =
    useState<Partial<ICustomerAddress> | null>(null);
  const [useShippingAddressForBilling, setUseShippingAddressForBilling] =
    useState(true);
  const [customerAddress, setCustomerAddress] =
    useState<ICustomerAddress | null>(null);
  const [selectedShippingMethod, setSelectedShippingMethod] =
    useState<IShippingMethod | null>(null);
  const [showAddressSelector, setShowAddressSelector] = useState(false);
  const [showAddressForm, setShowAddressForm] = useState(false);
  const [formErrors, setFormErrors] = useState<Record<string, string[]>>({});
  const [enabledPaymentMethods, setEnabledPaymentMethods] = useState<string[]>(
    [],
  );
  const [paymentMethod, setPaymentMethod] = useState(getDefaultPaymentMethod);
  const [expressElementsLoading, setExpressElementsLoading] = useState(true);
  const [stripePaymentElementReady, setStripePaymentElementReady] =
    useState(false);
  const [stripeElementsConfigurationKey, setStripeElementsConfigurationKey] =
    useState<string | null>(null);
  const [shippingAddressUpdating, setShippingAddressUpdating] = useState(false);
  const [shippingAddressUpdateFailed, setShippingAddressUpdateFailed] =
    useState(false);
  const [isCharityDonationExpanded, setIsCharityDonationExpanded] = useState(false);
  const stripePaymentAmount = checkoutTotals
    ? checkoutTotals.amountDueInCents > 0
      ? checkoutTotals.amountDueInCents
      : checkoutTotals.totalInCents
    : null;
  const stripeCurrency = checkout?.currencyCode.toLowerCase() ?? null;
  const isCheckoutLoading = cartLoading || totalsLoading;
  const isPaymentElementLoading = loading || isCheckoutLoading;
  const stripeElementsConfigurationKeyExpected =
    stripeCurrency && stripePaymentAmount !== null
      ? `${selectedStripePaymentMethod}-${stripePaymentAmount}-${stripeCurrency}`
      : null;
  const isPaymentDisabled =
    loading ||
    paymentInProgress ||
    isCheckoutLoading ||
    shippingAddressUpdating ||
    shippingAddressUpdateFailed ||
    (paymentMethod === "stripe" &&
      !!checkoutTotals &&
      checkoutTotals.amountDueInCents > 0 &&
      (!stripePaymentElementReady ||
        stripeElementsConfigurationKey !== stripeElementsConfigurationKeyExpected));

  const elements = useElements();
  const stripe = useStripe();
  const { state } = useLocation();
  const navigate = useNavigate();
  const expressElementsOptions = useMemo<StripeElementsOptions | null>(() => {
    if (!checkout || !checkoutTotals) {
      return null;
    }

    return {
      mode: "payment",
      amount:
        checkoutTotals.amountDueInCents > 0
          ? checkoutTotals.amountDueInCents
          : checkoutTotals.totalInCents,
      currency: checkout.currencyCode.toLowerCase(),
      appearance: config.stripeTheme,
      fonts: config.fonts,
    };
  }, [checkout, checkoutTotals]);
  useEffect(() => {
    if (paymentMethod !== "stripe") {
      setStripePaymentElementReady(false);
    }
  }, [paymentMethod]);

  useEffect(() => {
    if (
      !elements ||
      !stripeCurrency ||
      stripePaymentAmount === null ||
      !stripeElementsConfigurationKeyExpected ||
      isCheckoutLoading
    ) {
      return;
    }

    // Keep the Payment Element mounted while an address, shipping, discount, or
    // donation change recalculates totals. Stripe supports updating this
    // configuration in place; remounting the iframe clears entered card details.
    elements.update({
      amount: stripePaymentAmount,
      currency: stripeCurrency,
      paymentMethodTypes: [selectedStripePaymentMethod],
    });
    setStripeElementsConfigurationKey(stripeElementsConfigurationKeyExpected);
  }, [
    elements,
    isCheckoutLoading,
    selectedStripePaymentMethod,
    stripeElementsConfigurationKeyExpected,
    stripeCurrency,
    stripePaymentAmount,
  ]);

  useEffect(() => {
    if (!checkout) {
      return;
    }
    if (!shippingAddressUpdateFailed) {
      setError(null);
    }

    if (isErrorState(state)) {
      setError(state.error);
    }

    if (checkout.email) {
      setEmail(checkout.email);
      Sentry.setUser({
        email: checkout.email,
      });
    }

    if (!checkout.shippingAddress && !checkout.customer?.preferredAddress) {
      setManualShippingAddress({ country: config.countryCode });
      setShowAddressForm(true);
    } else if (checkout.shippingAddress && !checkout.shippingAddress?.id) {
      setManualShippingAddress(checkout.shippingAddress);
      setShowAddressForm(true);
    }
  }, [checkout, state, shippingAddressUpdateFailed]);

  useEffect(() => {
    if (email === null || email === "") {
      return;
    }
    const delayInputTimeoutId = setTimeout(() => {
      checkoutFormSchema
        .validateAt("email", { email })
        .then(() => {
          updateEmail(email).then(() => {});
        })
        .catch((error) => {
          setFormErrors({
            email: error.message,
          });
        });
    }, 1000);
    return () => clearTimeout(delayInputTimeoutId);
    /* TODO fix this */
    /* eslint-disable react-hooks/exhaustive-deps */
  }, [email]);
  /* eslint-enable react-hooks/exhaustive-deps */

  function handleEmailAddressChange(
    event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ) {
    const updatedFormErrors = { ...formErrors };
    delete updatedFormErrors.email;
    setFormErrors(updatedFormErrors);
    setEmail(event.target.value);
  }

  const handleShippingAddressChange = useCallback(
    async (shippingAddress: IShippingAddress | null) => {
      setManualShippingAddress(shippingAddress);
      setFormErrors({});
      setShippingAddressUpdating(true);
      try {
        Sentry.addBreadcrumb({
          category: "checkout",
          message: "Shipping address updated",
        });
        Sentry.setContext("checkout", checkout);
        await updateShippingAddress(
          checkout?.email ?? null,
          checkout?.phone ?? shippingAddress?.phone ?? null,
          shippingAddress,
        );
        setShippingAddressUpdateFailed(false);
        setError((currentError) =>
          currentError === addressDetailsErrorMessage ? null : currentError,
        );
        try {
          await trackingService.trackEvent("add_shipping_info", {
            checkout: checkout,
            totals: checkoutTotals,
          });
        } catch (trackingError) {
          console.error("Error tracking shipping address update:", trackingError);
        }
      } catch (e) {
        console.error("Error updating shipping address in cart:", e);
        setShippingAddressUpdateFailed(true);
        if (e instanceof CartAddressUserError) {
          const addressFieldErrors = mapCartAddressUserErrors(e.userErrors);
          setFormErrors((currentFormErrors) => ({
            ...currentFormErrors,
            shippingAddress: [addressDetailsErrorMessage],
            ...addressFieldErrors,
          }));
        }
        setError(addressDetailsErrorMessage);
      } finally {
        setShippingAddressUpdating(false);
      }
    },
    [checkout, checkoutTotals, updateShippingAddress],
  );

  const handleShippingAddressDraftChange = useCallback(
    (shippingAddress: Partial<IShippingAddress> | null) => {
      setManualShippingAddress(shippingAddress);
      const validFields = getLocallyValidAddressFields(shippingAddress);

      if (validFields.length === 0) {
        return;
      }

      setFormErrors((currentFormErrors) => {
        const nextFormErrors = { ...currentFormErrors };
        validFields.forEach((fieldName) => {
          delete nextFormErrors[fieldName];
        });
        return nextFormErrors;
      });
    },
    [],
  );

  const handleBillingAddressChange = useCallback(
    async (billingAddress: IShippingAddress | null) => {
      setManualBillingAddress(billingAddress);
    },
    [],
  );

  async function validateCheckout() {
    if (!checkout) {
      throw new Error("No checkout found");
    }
    await checkoutFormSchema.validate({ ...checkout, email }, { abortEarly: false });
  }

  function handleStripePaymentMethodSelect(nextPaymentMethod: string) {
    setSelectedStripePaymentMethod(nextPaymentMethod);
    setPaymentMethod("stripe");
  }

  async function handleGiftCardOnlyPayment() {
    if (!checkout || checkout.appliedGiftCards.length === 0) {
      return false;
    }
    const cartId = checkout.cartId.replace("gid://shopify/Cart/", "");
    navigate(`/checkout/confirm/${cartId}&gc=1`);
  }

  async function handlePaymentSubmit(event: React.SyntheticEvent) {
    event.preventDefault();
    if (!checkout) {
      throw new Error("Stripe not initialised");
    }
    if (shippingAddressUpdating || shippingAddressUpdateFailed) {
      return;
    }
    if (cartLoading || totalsLoading) {
      setError("Please wait for your order total to finish updating, then try again.");
      return;
    }
    setError(null);
    setLoading(true);
    setPaymentInProgress(true);

    if (config.validateDiscountCodesOnPayment) {
      try {
        const invalidDiscountCode = await validateDiscountCodes();
        if (invalidDiscountCode !== null) {
          setError(`Discount code "${invalidDiscountCode}" is not valid for this checkout`);
          setLoading(false);
          setPaymentInProgress(false);
          return;
        }
      } catch (error) {
        setError(error instanceof Error ? error.message : String(error));
        setLoading(false);
        setPaymentInProgress(false);
        return;
      }
    }

    // Validate address fields when the manual address form is visible
    if (showAddressForm) {
      try {
        await addressSchema.validate(manualShippingAddress, { abortEarly: false });
      } catch (e) {
        if (checkoutFormRef.current) {
          checkoutFormRef.current.scrollIntoView({ behavior: "smooth" });
        }
        const addressErrors: Record<string, string[]> = {};
        if (e && typeof e === 'object' && 'inner' in e && Array.isArray((e as any).inner)) {
          (e as ValidationError).inner.forEach((validationError: ValidationError) => {
            if (validationError.path) {
              addressErrors[validationError.path] = [validationError.message];
            }
          });
        }
        setFormErrors({
          ...formErrors,
          shippingAddress: ['Please complete the shipping address'],
          ...addressErrors,
        });
        setError("Please complete all required fields and try again.");
        setLoading(false);
        setPaymentInProgress(false);
        return;
      }
    }

    try {
      await validateCheckout();
    } catch (e) {
      if (checkoutFormRef.current) {
        checkoutFormRef.current.scrollIntoView({ behavior: "smooth" });
      }
      const validationErrors: Record<string, string[]> = {};
      if (e && typeof e === 'object' && 'inner' in e && Array.isArray(e.inner)) {
        e.inner.forEach((validationError: ValidationError) => {
          if (validationError.path) {
            validationErrors[validationError.path] = [validationError.message];
          }
        });
      }
      setFormErrors({
        ...formErrors,
        ...validationErrors,
      });
      console.error({ formErrors, validationErrors });
      setError("Please complete all required fields and try again.");
      setLoading(false);
      setPaymentInProgress(false);
      return;
    }

    try {
      if (checkout.appliedGiftCards.length > 0 && checkoutTotals?.amountDueInCents === 0) {
        await handleGiftCardOnlyPayment();
        return;
      }

      if (checkoutTotals) {
        try {
          await trackingService.trackGtmEvent("add_payment_info", {
            checkout: checkout,
            totals: checkoutTotals,
          });
        } catch (trackingError) {
          console.error("Error tracking payment info:", trackingError);
        }
      }

      if (paymentMethod === "stripe") {
        await handleStripePayment(checkout);
      }

      if (paymentMethod === "windcave" || paymentMethod === "farmlands") {
        await handleWindcavePayment(checkout);
      }
      if (paymentMethod === "gem") {
        await handleGemPayment(checkout);
      }
      if (paymentMethod === "poli") {
        await handlePoliPayment(checkout);
      }
    } catch (error) {
      console.error("Error processing payment", error);
      setError(getPaymentErrorMessage(paymentMethod, error));
    } finally {
      setLoading(false);
      setPaymentInProgress(false);
    }
  }

  async function handleStripePayment(checkout: ICheckout) {
    if (!stripe || !elements || !checkout) {
      throw new Error("Stripe not initialised");
    }
    const { error: submitError } = await elements.submit();
    if (submitError) {
      throw new Error(
        submitError.message ??
          "Something went wrong with your payment, please try again or contact support",
      );
    }

    const sessionResponse = await fetch(
      `${config.checkoutUrl}/api/createSession`,
      {
        method: "POST",
        body: JSON.stringify({
          cartId: checkout.cartId,
          provider: "stripe",
          paymentMethodTypes: [selectedStripePaymentMethod],
          expectedShippingCents: checkoutTotals
            ? Math.round(Number(checkoutTotals.shipping) * 100)
            : undefined,
        }),
      },
    );

    if (!sessionResponse.ok) {
      throw new Error(await sessionResponse.text());
    }

    const { sessionData } = await sessionResponse.json();
    const cartId = checkout.cartId.replace("gid://shopify/Cart/", "");
    const shippingAddress =
      checkout.shippingAddress ?? checkout.customer?.preferredAddress;
    const billingAddress = shippingAddress;

    // Confirm the PaymentIntent using the details collected by the Payment Element
    const { error } = await stripe.confirmPayment({
      elements,
      clientSecret: sessionData.sessionId,
      confirmParams: {
        return_url: `${config.checkoutUrl}/checkout/confirm/${cartId}`,
        shipping: {
          name: `${shippingAddress?.firstName} ${shippingAddress?.lastName}`,
          phone: shippingAddress?.phone ?? undefined,
          address: {
            line1: shippingAddress?.address1 ?? "",
            line2: shippingAddress?.address2 ?? undefined,
            city: shippingAddress?.city,
            state: shippingAddress?.province ?? "",
            postal_code: shippingAddress?.zip,
            country: config.countryCode,
          },
        },
        payment_method_data: {
          billing_details: {
            name: `${checkout.shippingAddress?.firstName} ${checkout.shippingAddress?.lastName}`,
            email: checkout.email ?? undefined,
            phone: billingAddress?.phone ?? undefined,
            address: {
              line1: billingAddress?.address1,
              line2: billingAddress?.address2 ?? undefined,
              city: billingAddress?.city,
              state: billingAddress?.province ?? "",
              postal_code: billingAddress?.zip,
              country: config.countryCode,
            },
          },
        },
      },
    });

    if (error) {
      throw new Error(
        error.message ??
          "Something went wrong with your payment, please try again or contact support",
      );
    }
  }

  async function handlePoliPayment(checkout: ICheckout) {
    const sessionResponse = await fetch(
      `${config.checkoutUrl}/api/createSession`,
      {
        method: "POST",
        body: JSON.stringify({
          cartId: checkout.cartId.replace("gid://shopify/Cart/", ""),
          provider: "poli",
        }),
      },
    );
    
    if (!sessionResponse.ok) {
      throw new Error("Unable to create POLi payment session");
    }
    const { sessionData } = await sessionResponse.json();
    window.location.href = sessionData.redirectUrl;
  }

  async function handleGemPayment(checkout: ICheckout) {
    const sessionResponse = await fetch(
      `${config.checkoutUrl}/api/createSession`,
      {
        method: "POST",
        body: JSON.stringify({
          cartId: checkout.cartId.replace("gid://shopify/Cart/", ""),
          provider: "gem",
        }),
      },
    );

    if (!sessionResponse.ok) {
      throw new Error("Unable to create gem payment session");
    }

    const { sessionData } = await sessionResponse.json();
    window.location.href = sessionData.redirectUrl;
  }

  async function handleWindcavePayment(checkout: ICheckout) {
    const sessionResponse = await fetch(
      `${config.checkoutUrl}/api/createSession`,
      {
        method: "POST",
        body: JSON.stringify({
          cartId: checkout.cartId.replace("gid://shopify/Cart/", ""),
          provider: "windcave",
        }),
      },
    );

    if (!sessionResponse.ok) {
      throw new Error("Unable to create Windcave payment session");
    }

    const { sessionData } = await sessionResponse.json();
    window.location.href = sessionData.redirectUrl;
  }

  async function handlePaypalPayment(checkout: ICheckout): Promise<string> {
    const sessionResponse = await fetch(
      `${config.checkoutUrl}/api/createSession`,
      {
        method: "POST",
        body: JSON.stringify({
          cartId: checkout.cartId.replace("gid://shopify/Cart/", ""),
          provider: "paypal",
        }),
      },
    );

    if (!sessionResponse.ok) {
      throw new Error("Unable to create Paypal payment session");
    }

    const { sessionData } = await sessionResponse.json();
    return sessionData.sessionId;
  }

  async function handleShippingMethodSelected(shippingMethod: IShippingMethod) {
    setSelectedShippingMethod(shippingMethod);
    await updateShippingMethod(shippingMethod.groupId, shippingMethod.handle);
  }

  async function handleExpressShippingAddressChange({
    address,
    resolve,
    reject,
  }: StripeExpressCheckoutElementShippingAddressChangeEvent) {
    try {
      if (!checkout) {
        reject();
        return;
      }

      let region = null;
      if (address.postal_code) {
        region = postcodeToRegion(address.postal_code, config.countryCode);
      }
      await updateShippingAddress(null, null, {
        firstName: "",
        lastName: "",
        address1: "",
        address2: "",
        city: address.city,
        province: region?.region ?? address.state ?? null,
        provinceCode: region?.code ?? config.defaultShippingProvince,
        zip: address.postal_code,
        country: address.country,
        phone: "",
      });
      const availableShippingMethods = checkout?.availableShippingMethods ?? [];
      if (availableShippingMethods.length > 0) {
        resolve({
          shippingRates: checkout.availableShippingMethods.map((method) => {
            const displayPrice = getShippingRateDisplayPrice(
              method,
              checkout.currencyCode,
              checkoutTotals?.shippingConversionRate,
            );
            return {
              id: method.handle,
              amount: Math.ceil(parseFloat(displayPrice.amount) * 100),
              displayName: method.title,
            };
          }),
        });
      } else {
        console.error("No shipping methods available for address", address);
        reject();
      }
    } catch (error) {
      console.error(error, address);
      reject();
    }
  }

  async function handlePaypalShippingAddress(
    data: OnShippingAddressChangeData,
    actions: OnShippingAddressChangeActions,
  ) {
    if (!checkout || !checkoutTotals) {
      return;
    }
    if (data.shippingAddress.countryCode !== config.countryCode) {
      return actions.reject();
    }
    try {
      const region = postcodeToRegion(data.shippingAddress.postalCode, config.countryCode);
      const shippingAddress = {
        firstName: checkout.shippingAddress?.firstName ?? "",
        lastName: checkout.shippingAddress?.lastName ?? "",
        address1: "",
        address2: null,
        city: data.shippingAddress?.city ?? "",
        province: region.region,
        provinceCode: region.code,
        zip: data.shippingAddress?.postalCode ?? "",
        country: data.shippingAddress?.countryCode ?? "",
        phone: "",
      };
      await updateShippingAddress(checkout.email, checkout.phone, shippingAddress);

      // ⚠️ BREAKING CHANGE: Validate PayPal order update success
      // Ensures shipping charges are properly applied before payment
      const updateResponse = await fetch("/api/paypalUpdateOrder", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          orderId: data.orderID,
          cartId: checkout?.cartId.replace("gid://shopify/Cart/", ""),
        }),
      });

      if (!updateResponse.ok) {
        console.error("PayPal order update failed:", updateResponse.status, updateResponse.statusText);
        setError("Failed to update PayPal order with shipping charges");
        return actions.reject();
      }

      // Validate response has expected structure
      const updateData = await updateResponse.json();
      if (!updateData.success) {
        console.error("PayPal order update returned failure:", updateData);
        setError("Failed to update PayPal order with shipping charges");
        return actions.reject();
      }
    } catch (e) {
      console.error(e);
      setError("Could not fetch shipping methods for Paypal");
      return actions.reject();
    }
  }

  const handlePaypalCapture = async (
    data: OnApproveData,
    actions: OnApproveActions,
  ) => {
    try {
      const response = await fetch("/api/confirmPayment", {
        method: "POST",
        body: JSON.stringify({
          provider: "paypal",
          externalId: data.orderID,
          cartId: checkout?.cartId.replace("gid://shopify/Cart/", ""),
        }),
      });

      const orderData = await response.json();

      const errorDetail = orderData?.details?.[0];

      if (errorDetail?.issue === "INSTRUMENT_DECLINED") {
        // Recoverable INSTRUMENT_DECLINED -> call actions.restart()
        // recoverable state, per https://developer.paypal.com/docs/checkout/standard/customize/handle-funding-failures/
        return actions.restart();
      } else if (errorDetail) {
        console.error(`Paypal failed with: ${errorDetail.description}`, {
          orderData,
          errorDetail,
        });
        throw new Error(
          `Could not complete your payment with PayPal: ${errorDetail.description}`,
        );
      } else {
        const cartId = checkout?.cartId.replace("gid://shopify/Cart/", "");
        Sentry.addBreadcrumb({
          category: "checkout",
          message: "Purchase completed",
        });
        Sentry.setContext("checkout", checkout);
        await trackingService.trackEvent("purchase", {
          checkout: checkout,
          totals: checkoutTotals,
        });
        navigate(`/checkout/complete/${cartId}&orderId=${data.orderID}`, {
          replace: true,
        });
      }
    } catch (error) {
      console.error(error);
      setError(`Could not complete your payment with PayPal: ${error}`);
    }
  };

  async function handleSelectCustomerAddress(address: IShippingAddress) {
    await updateCustomerPreferredAddress(address);
    setShowAddressSelector(false);
    setShowAddressForm(false);
  }

  async function handleLogout() {
    if (cartLoading) return;
    setLogoutError(null);
    try {
      await removeUserFromCart();
    } catch (e) {
      // Leave the logged-in state as it is: the cart still carries the
      // customer, so club pricing is still correct for what Shopify holds.
      setLogoutError(describeLogoutFailure(e));
      return;
    }
    setEmail("");
    setCustomerAddress(null);
    setSelectedShippingMethod(null);
  }

  if (!checkout || !checkoutTotals || !expressElementsOptions) {
    return null;
  }

  function handleAddNewCustomerAddress() {
    setCustomerAddress(null);
    setShowAddressForm(true);
  }

  return (
    <form onSubmit={handlePaymentSubmit} ref={checkoutFormRef} noValidate>
      <fieldset disabled={paymentInProgress}>
      <section className="mx-auto max-w-lg lg:max-w-none">
        {expressElementsLoading && (
          <div className="flex flex-col md:flex-row gap-3 animate-pulse">
            <div className="w-full">
              <span className="w-full h-12 block bg-gray-200 rounded-full"></span>
            </div>
            <div className="w-full">
              <span className="w-full h-12 block bg-gray-200 rounded-full"></span>
            </div>
            <div className="w-full">
              <span className="w-full h-12 block bg-gray-200 rounded-full"></span>
            </div>
          </div>
        )}
        <div className="flex flex-col-reverse md:flex-row">
          <Elements stripe={stripePromise} options={expressElementsOptions}>
            <CheckoutExpressPaymentMethods
              checkout={checkout}
              expectedShippingCents={checkoutTotals
                ? Math.round(Number(checkoutTotals.shipping) * 100)
                : undefined}
              enabledPaymentMethods={enabledPaymentMethods}
              setEnabledPaymentMethods={setEnabledPaymentMethods}
              expressElementsLoading={expressElementsLoading}
              setExpressElementsLoading={setExpressElementsLoading}
              handleExpressShippingAddressChange={
                handleExpressShippingAddressChange
              }
            />
          </Elements>
          {!expressElementsLoading && config.paymentEnabled.paypal && (
            <div
              className={classNames(
                enabledPaymentMethods.length === 2 ? "md:w-[32%]" : "",
                enabledPaymentMethods.length === 1 ? "md:w-[50%]" : "",
                enabledPaymentMethods.length > 0 ? "md:px-3" : "",
                "w-full",
              )}
            >
              <PayPalButtons
                style={{
                  shape: "pill",
                  layout: "vertical",
                  height: 50,
                }}
                createOrder={async () => await handlePaypalPayment(checkout)}
                onApprove={handlePaypalCapture}
                onShippingAddressChange={handlePaypalShippingAddress}
              />
            </div>
          )}
        </div>
        <div className="relative mt-8">
          <div
            className="absolute inset-0 flex items-center"
            aria-hidden="true"
          >
            <div className="w-full border-t border-gray-200" />
          </div>
          <div className="relative flex justify-center mb-4">
            <span className="bg-white px-4 text-sm font-medium text-gray-500">
              Or pay another way
            </span>
          </div>
        </div>
      </section>
      <section className="mx-auto max-w-lg lg:max-w-none">
        <section aria-labelledby="contact-info-heading">
          {checkout?.customer?.email && (
            <div className="p-3 bg-gray-50 rounded-2xl border border-gray-200">
              <span className="text-gray-900 block text-sm font-medium">
                {checkout?.customer?.email}
              </span>
              <button
                onClick={handleLogout}
                type="button"
                className="mt-2 text-sm underline text-blue-500"
                disabled={cartLoading}
              >
                {cartLoading ? "Logging out…" : "Logout"}
              </button>
              {logoutError && (
                <p className="mt-2 text-sm text-red-600" role="alert">
                  {logoutError}
                </p>
              )}
            </div>
          )}
          {!checkout?.customer?.email && (
            <>
              <TextInput
                name="email"
                type="email"
                label="Email address"
                placeholder="Email address"
                autoComplete="email"
                value={email}
                errors={formErrors}
                onChange={handleEmailAddressChange}
                onBlur={(event) => {
                  if (event.target.value !== email) {
                    handleEmailAddressChange(event);
                  }
                }}
              />
              <div className="mt-2 relative flex items-start">
                <div className="flex h-6 items-center">
                  <input
                    id="marketing_opt_in"
                    name="marketing_opt_in"
                    type="checkbox"
                    className="h-4 w-4 rounded border-gray-300 text-gray-600 focus:ring-gray-600"
                    checked={checkout?.emailMarketingOptIn}
                    onChange={() => toggleEmailMarketingOptIn()}
                  />
                </div>
                <div className="ml-3 text-sm leading-6">
                  <label
                    htmlFor="marketing_opt_in"
                    className="font-normal text-gray-900"
                  >
                    Subscribe to receive exclusive offers and news via email.
                  </label>
                </div>
              </div>
            </>
          )}
        </section>

        <section aria-labelledby="shipping-heading" className="relative mt-6">
          <h2
            id="delivery-heading"
            className="block text-md font-medium leading-6 text-gray-600"
          >
            Shipping address
          </h2>
          {showAddressForm && (
            <div className="transition duration-300 opacity-100">
              <AddressInput
                value={manualShippingAddress}
                onChange={handleShippingAddressChange}
                onDraftChange={handleShippingAddressDraftChange}
                hideOptIn={true}
                errors={formErrors}
              />
            </div>
          )}
          {checkout.customer && checkout.shippingAddress && checkout.shippingAddress.id &&
            !showAddressForm && (
              <div className="relative min-h-[100px]">
                <div
                  className={`absolute p-3 bg-gray-50 transition duration-300 rounded-2xl border border-gray-200 ${!showAddressSelector ? "opacity-100" : "opacity-0 -z-1 pointer-events-none"}`}
                >
                  <span className="text-gray-900 block text-sm font-medium">
                    {formatAddress(checkout.shippingAddress)}
                  </span>
                  <button
                    type="button"
                    className="mt-2 text-sm underline text-blue-500"
                    onClick={() => setShowAddressSelector(true)}
                  >
                    Change
                  </button>
                </div>
                <Transition
                  show={showAddressSelector}
                  enter="transition-opacity duration-300"
                  enterFrom="opacity-0"
                  enterTo="opacity-100"
                  leave="transition-opacity duration-75"
                  leaveFrom="opacity-100"
                  leaveTo="opacity-0"
                >
                  <AddressSelector
                    onChange={handleSelectCustomerAddress}
                    disabled={cartLoading}
                    value={
                      checkout.shippingAddress
                    }
                    addresses={checkout.customer.addresses}
                  />
                  <button
                    type="button"
                    className="mt-2 text-blue-400 underline font-medium"
                    onClick={handleAddNewCustomerAddress}
                  >
                    Add new address
                  </button>
                </Transition>
              </div>
            )}
        </section>
        <section aria-labelledby="delivery-heading" className="mt-6">
          <h2
            id="delivery-heading"
            className="block text-md font-medium leading-6 text-gray-600"
          >
            {checkout.shippingAddress ? "Shipping method" : "Estimated shipping"}
          </h2>
          {!checkout.shippingAddress && (
            <p className="text-sm text-gray-400">
              Enter your address for available shipping rates
            </p>
          )}
          {checkout.availableShippingMethods.length > 0 && (
            <ShippingRates
              rates={checkout.availableShippingMethods}
              currencyCode={checkout.currencyCode}
              shippingConversionRate={checkoutTotals?.shippingConversionRate}
              disabled={cartLoading || totalsLoading}
              selected={
                checkout.availableShippingMethods.some((method) => method.handle === selectedShippingMethod?.handle)
                  ? selectedShippingMethod
                  : checkout.shippingMethod
              }
              onShippingRateSelected={handleShippingMethodSelected}
            />
          )}
          {checkout.availableShippingMethods.length === 0 && (
            <div className="p-2 mt-2">
              <span className="text-sm text-gray-400">
                Enter shipping address to see available rates
              </span>
            </div>
          )}
        </section>
        <section>
          <div className="mt-2 relative flex items-start">
            <div className="flex h-6 items-center">
              <input
                id="use_shipping_address_for_billing_address"
                name="use_shipping_address_for_billing_address"
                type="checkbox"
                className="h-4 w-4 rounded border-gray-300 text-gray-600 focus:ring-gray-600"
                checked={useShippingAddressForBilling}
                onChange={(event) =>
                  setUseShippingAddressForBilling(!useShippingAddressForBilling)
                }
              />
            </div>
            <div className="ml-3 text-sm leading-6">
              <label
                htmlFor="use_shipping_address_for_billing_address"
                className="font-normal text-gray-900"
              >
                Use shipping address as billing address
              </label>
            </div>
          </div>
          {!useShippingAddressForBilling && (
            <div className="mt-2">
              <h2
                id="delivery-heading"
                className="block text-md font-medium leading-6 text-gray-600"
              >
                Billing address
              </h2>
              <div
                className={`transition duration-300 ${!customerAddress ? "opacity-100" : "h-0 opacity-0 -z-1 pointer-events-none"}`}
              >
                <AddressInput
                  value={manualBillingAddress}
                  onChange={handleBillingAddressChange}
                  hideOptIn={true}
                />
              </div>
            </div>
          )}
        </section>
        {checkoutTotals && checkoutTotals.amountDueInCents > 0 && (
          <section
            aria-labelledby="payment-heading"
            className="mt-6 border-gray-200"
          >
            <h2
              id="delivery-heading"
              className="block text-md font-medium leading-6 text-gray-600"
            >
              Payment method
            </h2>
            <div className="w-full pt-8">
              <div className="mx-auto w-full rounded-full bg-white">
                <div>
                  <div className="flex flex-col gap-2 py-6">
                    <div className="flex gap-2 justify-flex-start">
                      {config.paymentEnabled.farmlands && (
                        <button
                          onClick={() => setPaymentMethod("farmlands")}
                          type="button"
                          className={`${paymentMethod === "farmlands" ? "border-2 border-black text-black" : "text-gray-500"} text-left p-3 w-full border hover:text-black rounded-3xl text-xs font-bold`}
                        >
                          <img
                            alt="pay with Farmlands Card"
                            src="/logos/farmlands.png"
                            className="h-4 mb-1 rounded-md"
                          />
                          Farmlands
                        </button>
                      )}
                      {config.paymentEnabled.gem && (
                        <button
                          onClick={() => setPaymentMethod("gem")}
                          type="button"
                          className={`${paymentMethod === "gem" ? "border-2 border-black text-black" : "text-gray-500"} text-left p-3 w-full border hover:text-black rounded-3xl text-xs font-bold`}
                        >
                          <img
                            alt="pay with Gem"
                            src="/logos/gem.png"
                            className="h-4 mb-1 rounded-md"
                          />
                          Gem
                        </button>
                      )}
                      {config.paymentEnabled.poli && (
                        <button
                          onClick={() => setPaymentMethod("poli")}
                          type="button"
                          className={`${paymentMethod === "poli" ? "border-2 border-black text-black" : "text-gray-500"} text-left p-3 w-full border hover:text-black rounded-3xl text-xs font-bold`}
                        >
                          <img
                            alt="pay with POLi"
                            src="/logos/poli.svg"
                            className="h-4 mb-1 rounded-md"
                          />
                          POLi
                        </button>
                      )}
                    </div>
                    {config.paymentEnabled.stripe && (
                      <div className="flex gap-2 justify-flex-start">
                        <button
                          type="button"
                          onClick={() =>
                            handleStripePaymentMethodSelect("card")
                          }
                          className={`${paymentMethod === "stripe" && selectedStripePaymentMethod === "card" ? "border-2 border-black text-black" : "text-gray-500"} text-left p-3 w-full border hover:text-black rounded-3xl text-xs font-bold`}
                        >
                          <img
                            alt="pay with Card"
                            src="/logos/card.svg"
                            className="h-4 mb-1"
                          />
                          Card
                        </button>
                        {config.paymentEnabled.afterpay && (
                          <button
                            type="button"
                            onClick={() =>
                              handleStripePaymentMethodSelect(
                                "afterpay_clearpay",
                              )
                            }
                            className={`${paymentMethod === "stripe" && selectedStripePaymentMethod === "afterpay_clearpay" ? "border-2 border-black text-black" : "text-gray-500"} text-left p-3 w-full border hover:text-black rounded-3xl text-xs font-bold`}
                          >
                            <img
                              alt="pay with Afterpay"
                              src="/logos/afterpay.svg"
                              className="h-4 mb-1 rounded-md"
                            />
                            Afterpay
                          </button>
                        )}
                        {config.paymentEnabled.alipay && (
                          <button
                            type="button"
                            onClick={() =>
                              handleStripePaymentMethodSelect("alipay")
                            }
                            className={`${paymentMethod === "stripe" && selectedStripePaymentMethod === "alipay" ? "border-2 border-black text-black" : "text-gray-500"} text-left p-3 w-full border hover:text-black rounded-3xl text-xs font-bold`}
                          >
                            <img
                              alt="pay with Alipay"
                              src="/logos/alipay.svg"
                              className="h-4 mb-1 rounded-md"
                            />
                            Alipay
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                </div>
                {config.paymentEnabled.stripe && paymentMethod === "stripe" && (
                  <div className="relative min-h-[100px]">
                    <PaymentElement
                      onReady={() => setStripePaymentElementReady(true)}
                      options={{
                        paymentMethodOrder: [selectedStripePaymentMethod],
                        readOnly: isPaymentElementLoading,
                        layout: {
                          type: "tabs",
                          defaultCollapsed: false,
                          radios: true,
                          spacedAccordionItems: false,
                        },
                      }}
                    />
                    {isPaymentElementLoading && (
                      <div
                        className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-white/75 text-center"
                        aria-live="polite"
                      >
                        <LoadingSpinner />
                        <p className="text-sm font-medium text-gray-700">
                          {loading ? "Payment processing…" : "Updating your checkout…"}
                        </p>
                      </div>
                    )}
                  </div>
                )}
                <Transition
                  show={paymentMethod === "windcave"}
                  enter="transition-opacity duration-300"
                  enterFrom="opacity-0"
                  enterTo="opacity-100"
                  leave="transition-opacity duration-75"
                  leaveFrom="opacity-100"
                  leaveTo="opacity-0"
                >
                  <div className="transition min-h-[100px] border border-gray-300 rounded-3xl p-4">
                    <div className="font-bold flex align-center">
                      <img
                        alt="Windcave logo"
                        src="/logos/windcave.svg"
                        className="h-4 mb-1 mr-2"
                      />{" "}
                      Pay with Windcave
                    </div>
                    <p className="text-sm">
                      Click pay to proceed to Windcave payment form
                    </p>
                  </div>
                </Transition>
                <Transition
                  show={paymentMethod === "farmlands"}
                  enter="transition-opacity duration-300"
                  enterFrom="opacity-0"
                  enterTo="opacity-100"
                  leave="transition-opacity duration-0"
                  leaveFrom="opacity-100"
                  leaveTo="opacity-0"
                >
                  <div className="transition min-h-[100px] border border-gray-300 rounded-3xl p-4">
                    <div className="font-bold flex align-center">
                      <img
                        alt="Farmlands logo"
                        src="/logos/farmlands.png"
                        className="h-4 mb-1 mr-2"
                      />{" "}
                      Pay with Farmlands card
                    </div>
                    <p className="text-sm">
                      Click pay to proceed to Farmlands payment page
                    </p>
                    <p className="text-sm mt-2">
                      The Farmlands exclusive rebate will be applied on your
                      statement.
                    </p>
                  </div>
                </Transition>
                <Transition
                  show={paymentMethod === "poli"}
                  enter="transition-opacity duration-300"
                  enterFrom="opacity-0"
                  enterTo="opacity-100"
                  leave="transition-opacity duration-0"
                  leaveFrom="opacity-100"
                  leaveTo="opacity-0"
                >
                  <div className="transition min-h-[100px] border border-gray-300 rounded-3xl p-4">
                    <div className="font-bold flex align-center">
                      <img
                        alt="POLi logo"
                        src="/logos/poli.svg"
                        className="h-4 mb-1 mr-2"
                      />{" "}
                      Pay with POLi
                    </div>
                    <p className="text-sm">
                      Click pay to proceed to POLi payment form
                    </p>
                  </div>
                </Transition>
                <Transition
                  show={paymentMethod === "gem"}
                  enter="transition-opacity duration-300"
                  enterFrom="opacity-0"
                  enterTo="opacity-100"
                  leave="transition-opacity duration-75"
                  leaveFrom="opacity-100"
                  leaveTo="opacity-0"
                >
                  <div className="transition min-h-[100px] border border-gray-300 rounded-3xl p-4">
                    <div className="font-bold flex align-center">
                      <img
                        alt="Gem logo"
                        src="/logos/gem.png"
                        className="h-4 mb-1 mr-2"
                      />{" "}
                      Pay with Gem
                    </div>
                    <p className="text-sm">
                      Click pay to proceed to Gem payment form
                    </p>
                  </div>
                </Transition>
              </div>
            </div>
          </section>
        )}
        <div className="mt-6 border-t border-gray-200 pt-6">
          {error && (
            <div className="mt-5">
              <ErrorAlert message={error} />
            </div>
          )}
          {checkoutTotals && (
            <>
              <div className="block lg:hidden">
                <div className="flex justify-between">
                  <span>Order summary</span>
                  <button
                    type="button"
                    onClick={onShowDetails}
                    className="flex items-center mr-2 text-md text-gray-400 underline decoration-dotted"
                  >
                    Details
                  </button>
                </div>
                <dl className="mx-auto max-w-lg space-y-4 mt-6 border-b border-gray-200 pb-6">
                  <div className="flex text-sm items-center justify-between">
                    <dt className="text-gray-600">Cart Total</dt>
                    <dd>
                      {formatPrice(
                        checkoutTotals.carttotal,
                        checkout.currencyCode,
                      )}
                    </dd>
                  </div>
                  <div className="flex text-sm items-center justify-between">
                    <dt className="text-gray-600">Savings</dt>
                    <dd>
                      {formatPrice(
                        checkoutTotals.discounts,
                        checkout.currencyCode,
                      )}
                    </dd>
                  </div>
                  <CombinedCodeForm />
                  <div className="flex text-sm items-center justify-between">
                    <dt className="text-gray-600">Shipping</dt>
                    <dd>
                      {formatPrice(
                        checkoutTotals.shipping,
                        checkout.currencyCode,
                      )}
                    </dd>
                  </div>
                  {donationAmount > 0 && (
                    <div className="flex text-sm items-center justify-between">
                      <dt className="text-gray-600">Donation</dt>
                      <dd>
                        {formatPrice(donationAmount.toFixed(2), checkout.currencyCode)}
                      </dd>
                    </div>
                  )}
                  {charityDonation && <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
                    <button
                      type="button"
                      aria-expanded={isCharityDonationExpanded}
                      onClick={() => setIsCharityDonationExpanded((expanded) => !expanded)}
                      className="grid w-full grid-cols-3 items-center gap-4 border-b border-gray-200 px-5 py-4 text-left md:flex"
                    >
                      <div className="col-span-1 flex items-center justify-center self-stretch md:col-auto md:min-w-[20%] md:flex-1">
                        <img
                          src={charityDonation.imageUrl}
                          alt={`${charityDonation.name} logo`}
                          className="max-h-24 w-full object-contain"
                        />
                      </div>
                      <div className="col-span-2 flex min-w-0 items-center gap-3 md:col-auto md:min-w-[calc(80%-1rem)] md:flex-1">
                        <h3 className="text-sm font-semibold leading-snug text-gray-900">
                          Donate to {charityDonation.name}
                        </h3>
                        <svg
                          aria-hidden="true"
                          viewBox="0 0 20 20"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          className={`h-5 w-5 shrink-0 text-gray-500 transition-transform ${isCharityDonationExpanded ? "rotate-180" : ""}`}
                        >
                          <path d="m5 7.5 5 5 5-5" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      </div>
                    </button>
                    {isCharityDonationExpanded && <>
                      <div className="border-b border-gray-200">
                        <div className="min-w-0 px-4 py-4">
                          <div className="space-y-2 text-xs leading-relaxed text-gray-500">
                            {charityDonation.about.map((paragraph, index) => (
                              <p key={index}>{paragraph}</p>
                            ))}
                          </div>
                        </div>
                      </div>
                    </>}
                    <div role="radiogroup" aria-label="Donation amount" className="space-y-3 px-5 pb-5 pt-4">
                      <button
                        type="button"
                        role="radio"
                        aria-checked={donationOption === "round-up"}
                        onClick={() => onDonationOptionChange("round-up")}
                        className={`relative flex w-full cursor-pointer rounded-3xl border p-4 text-left focus:outline-none ${
                          donationOption === "round-up"
                            ? "z-10 border-gray-600 bg-gray-50"
                            : "border-gray-200 bg-gray-50"
                        }`}
                      >
                        <span className={`mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full border-2 bg-white ${donationOption === "round-up" ? "border-gray-600" : "border-gray-400"}`}>
                          {donationOption === "round-up" && <span className="h-2 w-2 rounded-full bg-gray-600" />}
                        </span>
                        <span className="ml-3 grid min-w-0 grid-cols-2 items-center gap-2">
                          <span className="min-w-0 text-sm font-medium text-gray-900">Round up to the nearest dollar</span>
                          <span className="min-w-0 text-xs font-light text-gray-700">
                            {totalsLoading ? (
                              <span aria-hidden="true" className="invisible">
                                Add <span className="font-semibold">${roundUpDonationAmount.toFixed(2)}</span> to bring your total to ${roundedUpCheckoutTotal.toFixed(2)}
                              </span>
                            ) : <>Add <span className="font-semibold">${roundUpDonationAmount.toFixed(2)}</span> to bring your total to ${roundedUpCheckoutTotal.toFixed(2)}</>}
                          </span>
                        </span>
                      </button>
                      <div
                        className="grid gap-2"
                        style={{ gridTemplateColumns: `repeat(${charityDonation.amounts.length + (charityDonation.allowCustomAmount ? 1 : 0)}, minmax(0, 1fr))` }}
                      >
                        {[...charityDonation.amounts, ...(charityDonation.allowCustomAmount ? ["Other"] : [])].map((amount) => (
                          <button
                            key={amount}
                            type="button"
                            role="radio"
                            aria-checked={donationOption === amount}
                            onClick={() => onDonationOptionChange(amount)}
                            className={`relative flex cursor-pointer items-center rounded-3xl border bg-gray-50 p-3 text-left focus:outline-none ${
                              donationOption === amount
                                ? "z-10 border-gray-600"
                                : "border-gray-200"
                            }`}
                          >
                            <span className={`flex h-4 w-4 shrink-0 items-center justify-center rounded-full border-2 bg-white ${donationOption === amount ? "border-gray-600" : "border-gray-400"}`}>
                              {donationOption === amount && <span className="h-2 w-2 rounded-full bg-gray-600" />}
                            </span>
                            <span className="ml-3 text-sm font-medium text-gray-900">{amount}</span>
                          </button>
                        ))}
                      </div>
                      {donationOption === "Other" && (
                        <div className="flex items-center gap-2 rounded-xl border border-gray-200 bg-gray-50 px-4 py-3">
                          <span className="text-sm font-medium text-gray-500">$</span>
                          <input
                            type="number"
                            min="0.01"
                            step="0.01"
                            value={otherDonationAmount}
                            onChange={(event) => onOtherDonationAmountChange(event.target.value)}
                            placeholder="Enter amount"
                            aria-label="Other donation amount"
                            className="min-w-0 flex-1 appearance-none bg-transparent text-sm text-gray-900 outline-none placeholder:text-gray-500 [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                          />
                        </div>
                      )}
                      {donationOption && (
                        <button
                          type="button"
                          onClick={() => {
                            onDonationOptionChange(null);
                            onOtherDonationAmountChange("");
                          }}
                          className="w-full text-center text-xs text-gray-500 underline underline-offset-2"
                        >
                          No thanks, I&apos;ll skip the donation
                        </button>
                      )}
                    </div>
                    {donationAmount > 0 && (
                      <div className="flex items-center justify-between gap-4 border-t border-gray-200 bg-secondary-50 px-5 py-4">
                        <div>
                          <p className="text-xs text-gray-500">Donation added</p>
                          <p className="text-base font-semibold text-gray-900">
                            +${donationAmount.toFixed(2)}{" "}
                            <span className="text-sm font-normal text-gray-500">to {charityDonation.name}</span>
                          </p>
                        </div>
                      </div>
                    )}
                  </div>}
                  <div className="flex text-md font-medium items-center justify-between">
                    <dt className="text-gray-600">Total</dt>
                    <dd>
                      {formatPrice(checkoutTotalWithDonation, checkout.currencyCode)}
                    </dd>
                  </div>
                  <div className="text-xs text-gray-700 font-light">
                    including{" "}
                    {formatPrice(checkoutTotals.tax, checkout.currencyCode)} in
                    taxes
                  </div>
                  <div className="flex text-md font-medium items-center justify-between">
                    <dt className="text-gray-600">Amount due</dt>
                    <dd>
                      {formatPrice(
                        checkoutTotals.amountDue,
                        checkout.currencyCode,
                      )}
                    </dd>
                  </div>
                </dl>
              </div>
              <LoadingButton
                disabled={isPaymentDisabled}
                loading={loading}
                loadingText="Payment Processing..."
                delayedLoadingText="You&apos;re in queue..."
                type="submit"
              >
                <span>Pay</span>
              </LoadingButton>
            </>
          )}
        </div>
      </section>
      </fieldset>
    </form>
  );
};

export default CheckoutForm;
